/**
 * Notes Domain Zod Validation Schemas
 *
 * Task: T-P5-D9-F3-1 (Foundation)
 *
 * Shared validation schemas for the notes domain.
 * Used for request body/query validation in both API and frontend.
 *
 * Note: These are Zod schemas for shared validation logic.
 * Fastify runtime validation uses JSON Schema (defined in notes.schemas.ts in the API).
 */

import { z } from 'zod';
import { NOTE_CATEGORIES, NOTE_PRIORITIES, NOTE_PIN_SCOPES } from './types.js';

// ============================================================================
// Reusable Primitives
// ============================================================================

const noteCategorySchema = z.enum(NOTE_CATEGORIES);
const notePrioritySchema = z.enum(NOTE_PRIORITIES);
const notePinScopeSchema = z.enum(NOTE_PIN_SCOPES);

/** Comma-separated string → array of validated enum values */
function commaSeparatedEnum<T extends readonly [string, ...string[]]>(
  enumValues: T,
) {
  return z
    .string()
    .transform((val) => val.split(',').map((s) => s.trim()).filter(Boolean))
    .pipe(z.array(z.enum(enumValues)).min(1));
}

// ============================================================================
// Note List Query Schema (GET /api/admin/notes)
// ============================================================================

export const noteListQuerySchema = z.object({
  search: z.string().optional(),
  category: commaSeparatedEnum(NOTE_CATEGORIES).optional(),
  priority: commaSeparatedEnum(NOTE_PRIORITIES).optional(),
  context: z.enum(['unlinked', 'client', 'session_event']).optional(),
  clientId: z.string().uuid().optional(),
  dateFrom: z.string().datetime({ offset: true }).optional(),
  dateTo: z.string().datetime({ offset: true }).optional(),
  hasReminder: z
    .union([z.boolean(), z.string().transform((v) => v === 'true' || v === '1')])
    .optional(),
  sort: z.enum(['recent', 'reminder']).default('recent'),
  cursor: z.string().optional(),
  limit: z
    .union([z.number(), z.string().transform(Number)])
    .pipe(z.number().int().min(1).max(100))
    .default(20),
});

export type NoteListQueryParsed = z.infer<typeof noteListQuerySchema>;

// ============================================================================
// Note CRUD Schemas
// ============================================================================

export const createNoteSchema = z.object({
  content: z.unknown().refine((v) => v !== undefined && v !== null, {
    message: 'content is required',
  }),
  contentPlain: z.string().min(1, 'contentPlain is required'),
  clientId: z.string().uuid().optional(),
  calendarEventId: z.string().uuid().optional(),
  templateId: z.string().uuid().optional(),
  category: noteCategorySchema.optional(),
  priority: notePrioritySchema.optional(),
  reminderDate: z.string().datetime({ offset: true }).optional(),
});

export type CreateNoteParsed = z.infer<typeof createNoteSchema>;

export const updateNoteSchema = z.object({
  content: z.unknown().optional(),
  contentPlain: z.string().min(1).optional(),
  clientId: z.string().uuid().nullable().optional(),
  calendarEventId: z.string().uuid().nullable().optional(),
  // templateId mirrors the create schema but is nullable so callers can
  // clear the applied template on an existing note (set to null) in addition
  // to swapping it for a different one.
  templateId: z.string().uuid().nullable().optional(),
  category: noteCategorySchema.optional(),
  priority: notePrioritySchema.optional(),
  reminderDate: z.string().datetime({ offset: true }).nullable().optional(),
});

export type UpdateNoteParsed = z.infer<typeof updateNoteSchema>;

// ============================================================================
// Pin Schemas
// ============================================================================

export const pinNoteSchema = z.object({
  scope: notePinScopeSchema,
  clientId: z.string().uuid().optional(),
  calendarEventId: z.string().uuid().optional(),
}).refine(
  (data) => {
    if (data.scope === 'CLIENT') return !!data.clientId;
    if (data.scope === 'CALENDAR_EVENT') return !!data.calendarEventId;
    return true;
  },
  { message: 'CLIENT scope requires clientId, CALENDAR_EVENT scope requires calendarEventId' },
);

export type PinNoteParsed = z.infer<typeof pinNoteSchema>;

export const unpinQuerySchema = z.object({
  scope: notePinScopeSchema,
  targetId: z.string().uuid().optional(),
});

export type UnpinQueryParsed = z.infer<typeof unpinQuerySchema>;

export const homeOrderSchema = z.object({
  orderedNoteIds: z.array(z.string().uuid()).min(1),
});

export type HomeOrderParsed = z.infer<typeof homeOrderSchema>;

// ============================================================================
// Client Notes Query Schema (GET /api/admin/clients/:id/notes)
// ============================================================================

export const clientNotesQuerySchema = z.object({
  cursor: z.string().optional(),
  limit: z
    .union([z.number(), z.string().transform(Number)])
    .pipe(z.number().int().min(1).max(100))
    .default(20),
  section: z.enum(['notes', 'mentionedIn']).optional(),
  /**
   * Free-text search across the client's own notes AND notes mentioning them.
   * Trimmed, and an all-whitespace value collapses to undefined so a blank box
   * reads as "no search" rather than "search for nothing".
   */
  search: z
    .string()
    .trim()
    .max(200)
    .optional()
    .transform((value) => (value ? value : undefined)),
});

export type ClientNotesQueryParsed = z.infer<typeof clientNotesQuerySchema>;

// ============================================================================
// Template Schemas
// ============================================================================

const templateSectionSchema = z.object({
  heading: z.string().trim().min(1, 'Section heading is required'),
  prompt: z.string().default(''),
});

export const createTemplateSchema = z.object({
  name: z.string().trim().min(1, 'Template name is required'),
  sections: z.array(templateSectionSchema).min(1, 'At least one section is required'),
});

export type CreateTemplateParsed = z.infer<typeof createTemplateSchema>;

export const updateTemplateSchema = z.object({
  name: z.string().trim().min(1, 'Template name is required').optional(),
  sections: z.array(templateSectionSchema).min(1, 'At least one section is required').optional(),
});

export type UpdateTemplateParsed = z.infer<typeof updateTemplateSchema>;

// ============================================================================
// Session Picker Query Schema (GET /api/admin/notes/session-picker) — T-P5-D9-F4-1
// ============================================================================

export const sessionPickerQuerySchema = z.object({
  search: z.string().trim().min(1).max(200).optional(),
  limit: z.coerce.number().int().min(1).max(50).default(20),
  cursor: z.string().optional(),
  // Optional client filter — Mode A narrows to 1:1 sessions with this client.
  clientId: z.string().uuid().optional(),
  // Sort key. Both values are startsAt-based so the existing cursor contract
  // still works. Title sort is NOT supported here (would require a different
  // cursor format).
  sortBy: z.enum(['recent', 'upcoming']).optional(),
  sortDir: z.enum(['asc', 'desc']).optional(),
});

export type SessionPickerQueryParsed = z.infer<typeof sessionPickerQuerySchema>;
